// ReportArtifact.jsx — G5 (#22): the in-thread home for a durable CUSTOM `name:'report'` directive.
//
// chatTimeline.mergeTimeline surfaces a report CUSTOM event as a { role:'directive', kind:'report',
// render } turn; AgentStudioChat renders that branch as <ReportArtifact render={t.render} />. This
// component is the report's "some in-thread home" the spec asks for: a compact card carrying the
// report `title` and an optional one-line `summary`. The body renders the report's markdown inline
// (there is no separate "full report" destination), so the card needs no view affordance.
//
// ISOLATION CONTRACT (mandatory): the markdown body reuses the A5 react-markdown module
// (AssistantMarkdown.jsx → the shared constrained, NO-rehype-raw COMPONENTS map). This file does
// NOT import react-markdown directly and adds NO second markdown library — model-authored report
// text is untrusted multi-tenant content, so it must flow through the one audited safe subset.
//
// render shape: the `render` prop is the namespaced `agent.report` render DIRECTIVE as emitted by
// the agent-service render_report tool — `{ name:"agent.report", version, payload:{ title, summary?,
// blocks?|markdown? } }` (the report content lives under `.payload`). We extract `.payload` here;
// for robustness we also accept a flat/bare `{ title, ... }` payload if a flatter envelope ever lands.
// `title` is required; `summary` is an optional one-liner; `markdown` (when present) is the body.

import React from "react"
import AssistantMarkdown from "./AssistantMarkdown.jsx"
// Item B — the pure create-campaign detail view-model, shared with the chat's ChatInteractionCard
// so the two approval surfaces cannot drift.
import { isCreateCampaign, createDetailRows, whyBlock, isListsendCall, listsendDetailRows } from "./approvalCreateDetails.js"

// S5: format a money value for an approval card's before/after line. The approval `summary` carries
// numeric `before_value`/`after_value` (USD; `0` is valid — a pause sets after_value to 0), so a
// number renders as a dollar figure and a missing value as an em-dash (never "[object Object]").
function fmtMoney(v) {
  if (typeof v === "number") return `$${v}`
  if (v == null || v === "") return "—"
  return String(v)
}

// S5: an `agent.approval` directive is NOT a report — its `payload.summary` is the five-field money
// line (verb/company/channel/before_value/after_value) that render_directive.rb validates. Render it
// as a compact, read-only money card so the SessionDrawer shows the load-bearing values (the gate
// asserts verb + before/after dollar values are present, not an empty row). NO approve affordance —
// the drawer is read-only; the approve action lives on the capability/interaction path.
function ApprovalArtifact({ payload }) {
  const title = payload.title || "Approval needed"
  const s = (payload && payload.summary) || {}

  // Item [B] — a create-campaign card has no meaningful money line (its summary is hardcoded
  // $0 → $0 at propose time). Render the create spec instead: objective / name / budget / why,
  // from payload.change — the SAME pure model the chat card uses. Pause/boost/activate/send fall
  // through to the proven money-line render below, unchanged.
  // A ListSend card (change.kind listsend_call) has no money line either ($0 → $0 says nothing):
  // render the server-built rows (what will happen / ListSend action / details) in the same layout.
  const listsendRows = isListsendCall(payload && payload.change) ? listsendDetailRows(payload) : null
  if (isCreateCampaign(payload && payload.change) || listsendRows) {
    const rows = listsendRows || createDetailRows(payload)
    const why = whyBlock(payload)
    return (
      <div className="st-report st-approval" role="group" aria-label={`Approval: ${title}`}>
        <div className="st-report__head">
          <span className="st-report__title">{title}</span>
        </div>
        <dl className="st-approval__summary st-approval__details">
          {rows.map((r) => (
            <div className="st-approval__row" key={r.key}>
              <dt className="st-approval__k">{r.key}</dt>
              <dd className="st-approval__v">{r.value}</dd>
            </div>
          ))}
          {why && why.summary && (
            <div className="st-approval__row st-approval__why">
              <dt className="st-approval__k">Why</dt>
              <dd className="st-approval__v">{why.summary}</dd>
            </div>
          )}
          {/* (B3) why.details bullets render on the board card too, matching the chat card —
              same helper, keeps the two surfaces non-divergent */}
          {why && why.details.length > 0 && (
            <ul className="st-approval__why-details">
              {why.details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}
        </dl>
      </div>
    )
  }

  return (
    <div className="st-report st-approval" role="group" aria-label={`Approval: ${title}`}>
      <div className="st-report__head">
        <span className="st-report__title">{title}</span>
      </div>
      <dl className="st-approval__summary">
        {s.verb != null && (
          <div className="st-approval__row">
            <dt className="st-approval__k">Action</dt>
            <dd className="st-approval__v st-approval__verb">{String(s.verb)}</dd>
          </div>
        )}
        {s.company != null && (
          <div className="st-approval__row">
            <dt className="st-approval__k">Company</dt>
            <dd className="st-approval__v">{String(s.company)}</dd>
          </div>
        )}
        {s.channel != null && (
          <div className="st-approval__row">
            <dt className="st-approval__k">Channel</dt>
            <dd className="st-approval__v">{String(s.channel)}</dd>
          </div>
        )}
        <div className="st-approval__row st-approval__money">
          <dt className="st-approval__k">Change</dt>
          <dd className="st-approval__v">
            <span className="st-approval__before">{fmtMoney(s.before_value)}</span>
            {" → "}
            <span className="st-approval__after">{fmtMoney(s.after_value)}</span>
          </dd>
        </div>
      </dl>
    </div>
  )
}

/**
 * Render a report directive as an in-thread card.
 * @param {{ render?: { name?: string, version?: number, payload?: object, title?: string, summary?: string, blocks?: Array, markdown?: string } }} props
 */
export default function ReportArtifact({ render }) {
  // The directive carries its content under `.payload` (agent.report directive); fall back to the
  // object itself if a bare payload is passed. This is the report-render reconciliation seam.
  const r = (render && render.payload) || render || {}

  // S5: an approval directive routes to the money-line card (its `summary` is an OBJECT, not a
  // prose string — rendering it as report `summary` would print "[object Object]").
  if (render && render.name === "agent.approval") {
    return <ApprovalArtifact payload={r} />
  }

  const title = r.title || "Report"
  const summary = r.summary
  // S6: a card-borne artifact (job 10 press release etc.) carries its body under `body`; a report
  // directive uses `markdown`. Accept either so the SAME card renders both — body flows through the
  // ONE audited AssistantMarkdown renderer (isolation contract preserved).
  const markdown = r.markdown || r.body
  // S6: optional outlet/reference links (e.g. the press-outlet shortlist). The job-10 gate asserts
  // the links render in the card drawer, so surface them beneath the body when present.
  const links = Array.isArray(r.links) ? r.links.filter((l) => l && l.url) : []

  return (
    <div className="st-report" role="group" aria-label={`Report: ${title}`}>
      <div className="st-report__head">
        <span className="st-report__title">{title}</span>
      </div>
      {summary ? <p className="st-report__summary">{summary}</p> : null}
      {markdown ? (
        <div className="st-report__body">
          <AssistantMarkdown text={markdown} />
        </div>
      ) : null}
      {links.length > 0 ? (
        <ul className="st-report__links">
          {links.map((l, i) => (
            <li key={i} className="st-report__link">
              <a href={l.url} target="_blank" rel="noopener noreferrer">{l.label || l.url}</a>
            </li>
          ))}
        </ul>
      ) : null}
      {/* Task 11 — a templated report was projected into the Artifacts library (DEC-11-A). Offer a
          link to the library (no per-artifact id is plumbed into the CUSTOM event in V1). */}
      {r.template ? (
        <div className="st-report__saved">
          <a href="/artifacts">Saved to AI Documents →</a>
        </div>
      ) : null}
    </div>
  )
}
